import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import logo from '../assets/OceanLogo.png';

interface RoomType {
  type: string;
  count: number;
}

interface BookingFormData {
  name: string;
  phone: string;
  email: string;
  adults: number;
  children: number;
  checkIn: string;
  checkOut: string;
  rooms: RoomType[];
  totalAmount: number;
  advanceAmount: number;
  remarks: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 32,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 15,
    textAlign: 'center',
    borderBottom: '1pt solid #1976d2',
    paddingBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 65,
    height: 65,
  },
  headerText: {
    marginLeft: 12,
  },
  hotelName: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#1976d2',
  },
  title: {
    fontSize: 13,
    color: '#666',
  },
  contentWrapper: {
    flexDirection: 'column',
    marginBottom: 10,
  },
  section: {
    marginBottom: 10,
    padding: 9,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 5,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4,
    padding: 3,
    backgroundColor: '#ffffff',
    borderRadius: 2,
  },
  label: {
    width: '40%',
    fontSize: 9,
    fontWeight: 'bold',
    color: '#333',
  },
  value: {
    width: '60%',
    fontSize: 9,
    color: '#555',
  },
  importantNotice: {
    marginTop: 8,
    marginBottom: 8,
    padding: 9,
    backgroundColor: '#fff4e5',
    borderRadius: 4,
    borderLeft: '3pt solid #ff9800',
  },
  importantNoticeTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#e65100',
    marginBottom: 3,
  },
  importantNoticeText: {
    fontSize: 9,
    color: '#555',
    lineHeight: 1.3,
    marginBottom: 2,
  },
  thankYou: {
    marginBottom: 10,
    padding: 9,
    backgroundColor: '#f0f7ff',
    borderRadius: 4,
  },
  thankYouTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 3,
  },
  thankYouText: {
    fontSize: 9,
    color: '#555',
    lineHeight: 1.3,
  },
  footer: {
    borderTop: '1pt solid #ddd',
    paddingTop: 5,
    textAlign: 'center',
  },
  footerText: {
    fontSize: 8,
    marginBottom: 1,
    color: '#666',
  },
  footerContact: {
    fontSize: 8,
    color: '#1976d2',
    fontWeight: 'bold',
  },
});

const BookingPDF = ({ data }: { data: BookingFormData }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Image src={logo} style={styles.logo} />
        <View style={styles.headerText}>
          <Text style={styles.hotelName}>Ocean Breeze Retreat</Text>
          <Text style={styles.title}>Booking Confirmation</Text>
        </View>
      </View>
      
      <View style={styles.contentWrapper}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Guest Information</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Guest Name:</Text>
            <Text style={styles.value}>{data.name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Phone Number:</Text>
            <Text style={styles.value}>{data.phone}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Email:</Text>
            <Text style={styles.value}>{data.email}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Stay Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Check-in:</Text>
            <Text style={styles.value}>{data.checkIn} (12:00 PM)</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Check-out:</Text>
            <Text style={styles.value}>{data.checkOut} (10:00 AM)</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Guests:</Text>
            <Text style={styles.value}>{data.adults} Adults, {data.children} Children</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Room & Payment Details</Text>
          {data.rooms.map((room, index) => (
            <View key={index} style={styles.row}>
              <Text style={styles.label}>{room.type} (s):</Text>
              <Text style={styles.value}>{room.count} (s)</Text>
            </View>
          ))}
          <View style={styles.row}>
            <Text style={styles.label}>Total Amount:</Text>
            <Text style={styles.value}>{data.totalAmount}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Advance Paid:</Text>
            <Text style={styles.value}>{data.advanceAmount}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Remaining Amount:</Text>
            <Text style={styles.value}>{data.totalAmount - data.advanceAmount}</Text>
          </View>
          {data.remarks && (
            <View style={styles.row}>
              <Text style={styles.label}>Remarks:</Text>
              <Text style={styles.value}>{data.remarks}</Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.importantNotice}>
        <Text style={styles.importantNoticeTitle}>Important Information:</Text>
        <Text style={styles.importantNoticeText}>• Remaining payment of {data.totalAmount - data.advanceAmount} must be made at check-in time.</Text>
        <Text style={styles.importantNoticeText}>• Valid government ID (Aadhar/DL/Passport) is mandatory for all guests.</Text>
      </View>

      <View style={styles.thankYou}>
        <Text style={styles.thankYouTitle}>Thank you for choosing Ocean Breeze Retreat!</Text>
        <Text style={styles.thankYouText}>We look forward to providing you with an exceptional stay. Our team is dedicated to making your experience comfortable and memorable.</Text>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>This is a computer-generated confirmation. No signature required.</Text>
        <Text style={styles.footerContact}>Contact: help@oceanbreezeretreat.in | +91-8093092360</Text>
      </View>
    </Page>
  </Document>
);

export default BookingPDF; 